import { Accordion, Card, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import dayjs from "dayjs";
import { RoleEnum, VehicleStateEnum } from "../../api";
import { OpportunityEntity } from "../../api/models/AccountEntity";
import { translateVehicleAvailabilityState, translateVehicleStates } from "../../utils/translations";
import { FORMAT_DATE_TIME } from "../../utils/constants";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const VehicleStatusAccordion = ({ vehicle, opportunity, statoVeicolo }: {
    vehicle: any,
    statoVeicolo: VehicleStateEnum | null,
    opportunity: OpportunityEntity | null
}) => {
    const { t } = useTranslation()
    const { userRole } = useAuth();
    const navigate = useNavigate();
    const builderCode =
        vehicle?.dati_veicolo?.manufacture_code ||
        vehicle?.dettagli_veicolo?.codice_costruttore;

    return (
        <Card className="mt-3">
            <Card.Body>
                <Row className="details-list m-0">
                    <Col sm={6}>
                        <small>	{t('vehicle_state').toUpperCase()}</small>
                        <div>{translateVehicleStates(statoVeicolo)}</div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('builder_code').toUpperCase()}</small>
                        <div>
                            {userRole === RoleEnum.MASTER && builderCode ? (
                                <span
                                    role="button"
                                    className="text-decoration-underline"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/offers?search=${encodeURIComponent(builderCode)}`)}
                                >
                                    {builderCode}
                                </span>
                            ) : (
                                builderCode || '-'
                            )}
                        </div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('vehicle_code', 'Codice Veicolo').toUpperCase()}</small>
                        <div>
                            {vehicle?.dati_veicolo?.vehicle_code || '-'}
                        </div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('vehicle_kind', 'Tipo veicolo').toUpperCase()}</small>
                        <div>
                            {vehicle?.dati_veicolo?.vehicle_kind || '-'}
                        </div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('availability').toUpperCase()}</small>
                        <div>
                            {translateVehicleAvailabilityState(vehicle?.dettagli_veicolo.stato_disp)}
                        </div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('city').toUpperCase()}</small>
                        <div>{vehicle?.dettagli_veicolo.city || '-'}</div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('expected_availability_date').toUpperCase()}</small>
                        <div>
                            {vehicle?.dettagli_veicolo?.data_disp
                                ? new Date(
                                    vehicle?.dettagli_veicolo?.data_disp
                                )?.toLocaleDateString('it')
                                : '-'}
                        </div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('factory_order_number').toUpperCase()}</small>
                        <div>
                            {userRole === RoleEnum.MASTER && vehicle?.dettagli_veicolo.nr_ordine ? (
                                <span
                                    role="button"
                                    className="text-decoration-underline"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/offers?search=${encodeURIComponent(vehicle.dettagli_veicolo.nr_ordine)}`)}
                                >
                                    {vehicle.dettagli_veicolo.nr_ordine}
                                </span>
                            ) : (
                                vehicle?.dettagli_veicolo.nr_ordine || '-'
                            )}
                        </div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('external_color').toUpperCase()} </small>
                        <div>{vehicle?.dettagli_veicolo.colore_esterno || '-'}</div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('list_price').toUpperCase()}</small>
                        <div className="text-nowrap">
                            {vehicle?.dati_veicolo.prezzo_listino
                                ? '€ ' +
                                vehicle?.dati_veicolo.prezzo_listino?.toLocaleString(
                                    'it',
                                    {
                                        minimumFractionDigits: 2,
                                    }
                                )
                                : '-'}
                        </div>
                    </Col>

                    {userRole === RoleEnum.MASTER && (
                        <Col sm={6}>
                            <small>{t('extra_list_price_discount_percentage').toUpperCase()}</small>
                            <div>
                                {vehicle?.dati_veicolo.extra_sconto_perc} %
                            </div>
                        </Col>
                    )}
                    <Col sm={6}>
                        <small>{t('internal_color').toUpperCase()}</small>
                        <div>{vehicle?.dettagli_veicolo.colore_interno || '-'}</div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('vehicle_frame').toUpperCase()}</small>
                        <div>{vehicle?.dettagli_veicolo.telaio || '-'}</div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('vehicle_body').toUpperCase()}</small>
                        <div>{vehicle?.dettagli_veicolo.carrozzeria || '-'}</div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('vehicle_registration_date').toUpperCase()}</small>
                        <div>
                            {vehicle?.dettagli_veicolo?.data_immatricolazione
                                ? new Date(
                                    vehicle?.dettagli_veicolo?.data_immatricolazione
                                )?.toLocaleDateString('it')
                                : '-'}
                        </div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('vehicle_plate').toUpperCase()}</small>
                        <div>{vehicle?.dettagli_veicolo.targa || '-'}</div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('id_bo').toUpperCase()}</small>
                        <div>{vehicle?.dettagli_veicolo?.id_bo || '-'}</div>
                    </Col>
                    <Col sm={6}>
                        <small>{t('notes').toUpperCase()}</small>
                        <div>{vehicle?.dettagli_veicolo?.note || '-'}</div>
                    </Col>
                </Row>

                {/* DETT OPZIONE *************************************************/}

                <Accordion className="mt-4 technical-data" flush>
                    {[VehicleStateEnum.OPZIONATO, VehicleStateEnum.OSCURATO].includes(
                        vehicle?.stato_corrente?.type
                    ) ? (
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>{t('option_details')}</Accordion.Header>
                            <Accordion.Body>
                                <Row className="details-list">
                                    <Col sm={6}>
                                        <small>{t('option_user')}</small>
                                        <div>{vehicle?.stato_corrente?.utente || '-'}</div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{t('option_dealer')}</small>
                                        <div>
                                            {vehicle?.stato_corrente?.dealer_utente || '-'}
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{opportunity ? i18next.t('account') : i18next.t('customer')}</small>
                                        <div>
                                            {opportunity ? (
                                                <span>
                                                    <a
                                                        href={`/my-accounts/${opportunity.AccountId}`}
                                                    >
                                                        {vehicle?.stato_corrente?.cliente || '-'}
                                                    </a>
                                                </span>
                                            ) : (
                                                <span>
                                                    {vehicle?.stato_corrente?.cliente || '-'}
                                                </span>
                                            )}
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{t('business_name')}</small>
                                        <div>
                                            {vehicle?.stato_corrente?.ragione_sociale || '-'}
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{t('credit_application')}</small>
                                        <div>
                                            {vehicle?.stato_corrente?.pratica_credito || '-'}
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{t('number_extensions')}</small>
                                        <div>
                                            {vehicle?.stato_corrente?.n_estensione || '0'}
                                        </div>
                                    </Col>

                                    {vehicle?.stato_corrente?.type ===
                                        VehicleStateEnum.OPZIONATO && (
                                            <>
                                                <Col sm={6}>
                                                    <small>{t('option_start_date').toUpperCase()}</small>
                                                    <div>
                                                        {vehicle?.stato_corrente?.data_inizio
                                                            ? new Date(
                                                                vehicle?.stato_corrente?.data_inizio
                                                            )?.toLocaleDateString('it')
                                                            : '-'}
                                                    </div>
                                                </Col>
                                                <Col sm={6}>
                                                    <small>{t('option_expiration_date').toUpperCase()}</small>
                                                    <div>
                                                        {vehicle?.stato_corrente?.data_scadenza
                                                            ? new Date(
                                                                vehicle?.stato_corrente?.data_scadenza
                                                            )?.toLocaleDateString('it')
                                                            : '-'}
                                                    </div>
                                                </Col>
                                            </>
                                        )}

                                    {opportunity && (
                                        <>
                                            <Col sm={6}>
                                                <small>{t('opportunity_name').toUpperCase()}</small>
                                                <div>
                                                    <a href={`/opportunities/${opportunity.Id}`}>
                                                        {opportunity.Name}
                                                    </a>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <small>{t('rental_company').toUpperCase()}</small>
                                                <div>{opportunity.Noleggiatore__c}</div>
                                            </Col>
                                            <Col sm={6}>
                                                <small>{t('opportunity_creation_date').toUpperCase()}</small>
                                                <div>
                                                    {dayjs(opportunity.CreatedDate).format(
                                                        FORMAT_DATE_TIME
                                                    )}
                                                </div>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </Accordion.Body>
                        </Accordion.Item>
                    ) : (
                        ''
                    )}

                    {/* DETTAGLI VENDITA */}
                    {vehicle?.stato_corrente?.type === VehicleStateEnum.VENDUTO ? (
                        <Accordion.Item eventKey="3">
                            <Accordion.Header>{t('sale_details')}</Accordion.Header>
                            <Accordion.Body>
                                <Row className="details-list">
                                    <Col sm={6}>
                                        <small>{t('sale_user')}</small>
                                        <div>{vehicle?.stato_corrente?.utente || '-'}</div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{opportunity ? i18next.t('account') : i18next.t('user')}</small>
                                        <div>
                                            {opportunity ? (
                                                <span>
                                                    <a
                                                        href={`/my-accounts/${opportunity.AccountId}`}
                                                    >
                                                        {vehicle?.stato_corrente?.cliente || '-'}
                                                    </a>
                                                </span>
                                            ) : (
                                                <span>
                                                    {vehicle?.stato_corrente?.cliente || '-'}
                                                </span>
                                            )}
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{t('business_name')}</small>
                                        <div>
                                            {vehicle?.stato_corrente?.ragione_sociale || '-'}
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{t('credit_application')}</small>
                                        <div>
                                            {vehicle?.stato_corrente?.pratica_credito || '-'}
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <small>{t('sale_date').toUpperCase()}</small>
                                        <div>
                                            {vehicle?.stato_corrente?.data_inizio
                                                ? new Date(
                                                    vehicle?.stato_corrente?.data_inizio
                                                )?.toLocaleDateString('it')
                                                : '-'}
                                        </div>
                                    </Col>
                                    {opportunity && (
                                        <>
                                            <Col sm={6}>
                                                <small>{t('opportunity_name').toUpperCase()}</small>
                                                <div>
                                                    <a href={`/opportunities/${opportunity.Id}`}>
                                                        {opportunity.Name}
                                                    </a>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <small>{t('rental_company').toUpperCase()}</small>
                                                <div>{opportunity.Noleggiatore__c}</div>
                                            </Col>
                                            <Col sm={6}>
                                                <small>{t('opportunity_creation_date').toUpperCase()}</small>
                                                <div>
                                                    {dayjs(opportunity.CreatedDate).format(
                                                        FORMAT_DATE_TIME
                                                    )}
                                                </div>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </Accordion.Body>
                        </Accordion.Item>
                    ) : (
                        ''
                    )}
                </Accordion>
            </Card.Body>
        </Card>
    )
}

export default VehicleStatusAccordion
